import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Save, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import AiFileDropzone, { AiAnalyzeButton } from "../../components/ai/AiFileDropzone";
import { akilEvrakAnaliz, akilEvrakDosyaGuncelle } from "../../lib/akilApi";
import { getAkilContext } from "../../lib/akilContext";
import { crud } from "../../lib/api";
import {
    KIMLIK_TIPI_OPTIONS,
    inferKimlikTipi,
    kimlikNoLabel,
    normalizeKimlikNo,
    validateMuvekkilKimlik,
} from "../../lib/muvekkilKimlik";

const MUVEKKIL_FIELDS = [
    ["ad", "Ad"],
    ["soyad", "Soyad"],
    ["kimlik_tipi", "Kimlik Türü"],
    ["tc_kimlik_no", "T.C. Kimlik No"],
    ["telefon", "Telefon"],
    ["email", "E-posta"],
    ["iban", "IBAN"],
    ["adres", "Adres"],
];

const SIGORTA_FIELDS = [
    ["plaka", "Plaka"],
    ["marka", "Marka"],
    ["model", "Model"],
    ["model_yili", "Model Yılı"],
    ["sase", "Şasi No (VIN)"],
    ["motor_no", "Motor No"],
    ["kaza_tarihi", "Kaza Tarihi"],
    ["kusur_orani", "Kusur Oranı (%)"],
    ["sigorta_sirketi", "Sigorta Şirketi"],
];

function emptyMuvekkil() {
    const base = Object.fromEntries(MUVEKKIL_FIELDS.map(([k]) => [k, ""]));
    base.kimlik_tipi = "TC";
    return base;
}

function emptySigorta() {
    return Object.fromEntries(SIGORTA_FIELDS.map(([k]) => [k, ""]));
}

function mapValues(raw, fields) {
    const src = raw || {};
    const out = {};
    fields.forEach(([k]) => {
        let v = src[k];
        if ((v === null || v === undefined || v === "") && k === "ad") {
            v = src.adi || src.adı || src.isim;
        }
        if ((v === null || v === undefined || v === "") && k === "soyad") {
            v = src.soyadi || src.soyadı;
        }
        if ((v === null || v === undefined || v === "") && k === "tc_kimlik_no") {
            v = src.tc || src.tckn || src.kimlik_no;
        }
        if ((v === null || v === undefined || v === "") && k === "sase") {
            v = src.sase_no || src.sasi || src.sasi_no || src.vin || src.chassis;
        }
        if ((v === null || v === undefined || v === "") && k === "motor_no") {
            v = src.motor || src.motor_numarasi || src.engine_no || src.engine;
        }
        if ((v === null || v === undefined || v === "") && k === "plaka") {
            v = src.plate || src.arac_plakasi;
        }
        out[k] = v === null || v === undefined ? "" : String(v);
    });
    const full = src.ad_soyad || src.adsoyad;
    if (full && (!out.ad || !out.soyad)) {
        const parts = String(full).trim().split(/\s+/);
        if (parts.length >= 2) {
            if (!out.ad) out.ad = parts[0];
            if (!out.soyad) out.soyad = parts.slice(1).join(" ");
        }
    }
    if (!out.kimlik_tipi && out.tc_kimlik_no) {
        out.kimlik_tipi = inferKimlikTipi(out.tc_kimlik_no);
    }
    if (out.tc_kimlik_no) {
        out.tc_kimlik_no = normalizeKimlikNo(
            out.tc_kimlik_no,
            out.kimlik_tipi || "TC",
        );
    }
    return out;
}

export default function AkilEvrakScanPage({ mode: modeProp }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const mode = modeProp || (location.pathname.includes("dosya") ? "sigorta" : "muvekkil");
    const isMuvekkil = mode === "muvekkil";

    const [file, setFile] = useState(null);
    const [belgeTipi, setBelgeTipi] = useState(
        () => (location.pathname.includes("muvekkil") ? "kimlik" : "otomatik"),
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [ozet, setOzet] = useState("");
    const [hamMetin, setHamMetin] = useState("");
    const [dogrulama, setDogrulama] = useState(null);
    const [muvekkil, setMuvekkil] = useState(emptyMuvekkil());
    const [sigorta, setSigorta] = useState(emptySigorta());
    const [extraMuvekkilId, setExtraMuvekkilId] = useState("");
    const [guncelleDosyaId, setGuncelleDosyaId] = useState("");

    useEffect(() => {
        const ctx = getAkilContext();
        const q = searchParams.get("dosya_id") || searchParams.get("sigorta_dosyasi_id");
        if (q) setGuncelleDosyaId(String(q));
        else if (ctx?.sigorta_dosyasi_id) setGuncelleDosyaId(String(ctx.sigorta_dosyasi_id));
    }, [searchParams]);

    const fields = useMemo(() => (isMuvekkil ? MUVEKKIL_FIELDS : SIGORTA_FIELDS), [isMuvekkil]);
    const values = isMuvekkil ? muvekkil : sigorta;
    const setValues = isMuvekkil ? setMuvekkil : setSigorta;

    const analyze = async () => {
        if (!file) {
            toast.error("Önce bir dosya seçin.");
            return;
        }
        setLoading(true);
        try {
            const data = await akilEvrakAnaliz(file, belgeTipi);
            if (!data.success) throw new Error(data.error || "Analiz başarısız.");
            setMuvekkil(mapValues(data.muvekkil_onerileri, MUVEKKIL_FIELDS));
            setSigorta(mapValues(data.sigorta_onerileri, SIGORTA_FIELDS));
            setOzet(data.tespit_ozeti || "");
            setHamMetin(data.ham_metin || "");
            setDogrulama(data.dogrulama || null);
            if (data.dogrulama?.uyari) {
                toast.warning(data.dogrulama.uyari, { duration: 8000 });
            }
            toast.success("Belge okundu — alanları kontrol edip kaydedin.");
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || "OCR/AI hatası");
        } finally {
            setLoading(false);
        }
    };

    const guncelleMevcut = async () => {
        if (!guncelleDosyaId.trim()) {
            toast.error("Güncellenecek dosya ID girin.");
            return;
        }
        setSaving(true);
        try {
            const alanlar = { ...sigorta };
            ["model_yili", "kusur_orani"].forEach((k) => {
                if (alanlar[k] !== "" && alanlar[k] != null) alanlar[k] = Number(alanlar[k]);
            });
            Object.keys(alanlar).forEach((k) => {
                if (alanlar[k] === "" || alanlar[k] === null) delete alanlar[k];
            });
            const evrakMap = {
                ruhsat: "RUHSAT",
                kaza_tutanagi: "TUTANAK",
                vekaletname: "VEKALET",
            };
            const data = await akilEvrakDosyaGuncelle({
                sigorta_dosyasi_id: Number(guncelleDosyaId),
                alanlar,
                evrak_turu: evrakMap[belgeTipi] || undefined,
            });
            if (!data.success) throw new Error(data.error);
            toast.success(`Dosya güncellendi — kalan eksik: ${(data.eksik_guncel || []).join(", ") || "yok"}`);
        } catch (err) {
            toast.error(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isMuvekkil) {
                if (!muvekkil.ad?.trim() || !muvekkil.soyad?.trim()) {
                    toast.error("Ad ve soyad zorunlu.");
                    setSaving(false);
                    return;
                }
                const tip = muvekkil.kimlik_tipi || inferKimlikTipi(muvekkil.tc_kimlik_no);
                const kimlikCheck = validateMuvekkilKimlik(tip, muvekkil.tc_kimlik_no);
                if (!kimlikCheck.ok) {
                    toast.error(kimlikCheck.message);
                    setSaving(false);
                    return;
                }
                const payload = {
                    ...muvekkil,
                    kimlik_tipi: tip,
                    tc_kimlik_no: kimlikCheck.value,
                };
                Object.keys(payload).forEach((k) => {
                    if (payload[k] === "") delete payload[k];
                });
                const created = await crud.create("muvekkiller", payload);
                toast.success(`Müvekkil kaydedildi: ${created.ad} ${created.soyad}`);
                setExtraMuvekkilId(String(created.id));
            } else {
                const payload = { ...sigorta };
                if (extraMuvekkilId) payload.muvekkil = Number(extraMuvekkilId);
                if (!payload.muvekkil) {
                    toast.error("Sigorta dosyası için müvekkil ID girin veya önce müvekkil kaydedin.");
                    setSaving(false);
                    return;
                }
                ["model_yili", "kusur_orani"].forEach((k) => {
                    if (payload[k] !== "" && payload[k] != null) payload[k] = Number(payload[k]);
                });
                Object.keys(payload).forEach((k) => {
                    if (payload[k] === "" || payload[k] === null) delete payload[k];
                });
                if (!payload.plaka) {
                    toast.error("Plaka alanı gerekli.");
                    setSaving(false);
                    return;
                }
                const required = ["marka", "model", "model_yili", "km"];
                required.forEach((k) => {
                    if (payload[k] === undefined) payload[k] = k === "km" ? 0 : payload[k] || "-";
                });
                const created = await crud.create("sigorta-dosyalari", payload);
                toast.success(`Dosya açıldı: ${created.dosya_no || created.id}`);
            }
        } catch (err) {
            const d = err.response?.data;
            const msg =
                d?.detail ||
                (d && typeof d === "object" ? JSON.stringify(d) : null) ||
                err.message;
            toast.error(String(msg));
        } finally {
            setSaving(false);
        }
    };

    return (
        <AiPageShell
            title={isMuvekkil ? "Resimden Müvekkil Kaydı" : "Resimden Sigorta Dosyası"}
            subtitle="Kimlik, ruhsat veya kaza evrakını yükleyin; Akıl OCR + AI ile alanları doldursun."
            actions={
                <Link
                    to={isMuvekkil ? "/ai/resimden-dosya" : "/ai/resimden-muvekkil"}
                    className="text-xs px-3 py-1.5 rounded-lg border border-white/15 text-slate-400 hover:text-white"
                >
                    {isMuvekkil ? "Dosya moduna geç" : "Müvekkil moduna geç"}
                </Link>
            }
        >
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <AiFileDropzone
                        onFile={setFile}
                        label={
                            isMuvekkil
                                ? "Kimlik, vekalet veya müvekkil bilgi belgesi"
                                : "Ruhsat, kaza tutanağı veya poliçe belgesi"
                        }
                    />
                    <label className="block text-xs text-slate-400">
                        Belge tipi
                        <select
                            value={belgeTipi}
                            onChange={(e) => setBelgeTipi(e.target.value)}
                            className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                        >
                            <option value="otomatik">Otomatik algıla</option>
                            <option value="ruhsat">Ruhsat</option>
                            <option value="kaza_tutanagi">Kaza tutanağı</option>
                            <option value="vekaletname">Vekaletname</option>
                            <option value="kimlik">Kimlik</option>
                        </select>
                    </label>
                    <AiAnalyzeButton loading={loading} onClick={analyze}>
                        Belgeyi Oku (OCR + AI)
                    </AiAnalyzeButton>
                    {ozet ? (
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-slate-300">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Tespit özeti</p>
                            {ozet}
                        </div>
                    ) : null}
                    {dogrulama?.uyari ? (
                        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                            {dogrulama.uyari}
                        </div>
                    ) : null}
                    {hamMetin ? (
                        <details className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-slate-400">
                            <summary className="cursor-pointer text-slate-500 uppercase tracking-wider">
                                OCR ham metin (doğrulama)
                            </summary>
                            <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-slate-400">
                                {hamMetin}
                            </pre>
                        </details>
                    ) : null}
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#121A2F]/60 p-5 space-y-4">
                    <h2 className="text-sm font-semibold text-[#F3E5AB]">
                        {isMuvekkil ? "Müvekkil alanları" : "Sigorta dosyası alanları"}
                    </h2>

                    {!isMuvekkil ? (
                        <label className="block text-xs text-slate-400">
                            Mevcut dosyayı güncelle (ID)
                            <input
                                value={guncelleDosyaId}
                                onChange={(e) => setGuncelleDosyaId(e.target.value)}
                                placeholder="Dosya detayından otomatik dolar"
                                className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm"
                            />
                        </label>
                    ) : null}

                    {!isMuvekkil ? (
                        <label className="block text-xs text-slate-400">
                            Müvekkil ID (yeni dosya için)
                            <input
                                value={extraMuvekkilId}
                                onChange={(e) => setExtraMuvekkilId(e.target.value)}
                                placeholder="Örn: OCR ile yeni müvekkil kaydettiyseniz ID"
                                className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm"
                            />
                        </label>
                    ) : null}

                    <div className="grid sm:grid-cols-2 gap-3">
                        {fields.map(([name, label]) => {
                            if (isMuvekkil && name === "kimlik_tipi") {
                                return (
                                    <label key={name} className="block text-xs text-slate-400">
                                        {label}
                                        <select
                                            value={values.kimlik_tipi || "TC"}
                                            onChange={(e) =>
                                                setMuvekkil((s) => ({
                                                    ...s,
                                                    kimlik_tipi: e.target.value,
                                                    tc_kimlik_no: normalizeKimlikNo(
                                                        s.tc_kimlik_no,
                                                        e.target.value,
                                                    ),
                                                }))
                                            }
                                            className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                                        >
                                            {KIMLIK_TIPI_OPTIONS.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }
                            const fieldLabel =
                                isMuvekkil && name === "tc_kimlik_no"
                                    ? kimlikNoLabel(values.kimlik_tipi || "TC")
                                    : label;
                            return (
                                <label
                                    key={name}
                                    className={`block text-xs text-slate-400 ${
                                        name === "adres" || name === "sigorta_sirketi" || name === "sase"
                                            ? "sm:col-span-2"
                                            : ""
                                    }`}
                                >
                                    {fieldLabel}
                                    <input
                                        value={values[name] || ""}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (isMuvekkil && name === "tc_kimlik_no") {
                                                const tip = values.kimlik_tipi || "TC";
                                                setMuvekkil((s) => ({
                                                    ...s,
                                                    tc_kimlik_no: normalizeKimlikNo(raw, tip),
                                                }));
                                                return;
                                            }
                                            setValues((s) => ({ ...s, [name]: raw }));
                                        }}
                                        className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                                    />
                                </label>
                            );
                        })}
                    </div>

                    {!isMuvekkil && (
                        <div className="rounded-lg border border-white/5 bg-black/20 p-3 text-xs text-slate-500">
                            Müvekkil alanları da okunduysa{" "}
                            <Link to="/ai/resimden-muvekkil" className="text-[#D4AF37] underline">
                                müvekkil kaydı
                            </Link>{" "}
                            oluşturup ID&apos;yi buraya yapıştırabilirsiniz.
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-2">
                        {!isMuvekkil && guncelleDosyaId ? (
                            <button
                                type="button"
                                disabled={saving}
                                onClick={guncelleMevcut}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D4AF37]/40 text-[#F3E5AB] text-sm font-bold disabled:opacity-50"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                Mevcut Dosyayı Güncelle
                            </button>
                        ) : null}
                        <button
                            type="button"
                            disabled={saving}
                            onClick={save}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4AF37] text-black text-sm font-bold disabled:opacity-50"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            {isMuvekkil ? "Müvekkil Kaydet" : "Sigorta Dosyası Aç"}
                        </button>
                        <Link
                            to={`/e/${isMuvekkil ? "muvekkiller" : "sigorta-dosyalari"}`}
                            className="inline-flex items-center gap-1 px-3 py-2 text-xs text-slate-400 hover:text-white"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Modül listesi
                        </Link>
                    </div>
                </div>
            </div>
        </AiPageShell>
    );
}
